import { useState, useEffect } from "react";
import { apiClient } from "../api/client";
import Card from "../components/Card";

interface Team {
  id: string;
  name: string;
  description?: string;
  managerId?: string;
  createdAt?: string;
  updatedAt?: string;
}

interface TeamFormData {
  name: string;
  description?: string;
  managerId?: string;
}

function TeamManagement() {
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingTeam, setEditingTeam] = useState<Team | null>(null);
  const [formData, setFormData] = useState<TeamFormData>({
    name: "",
    description: undefined,
    managerId: undefined,
  });

  // Fetch teams
  const fetchTeams = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get<{ teams: Team[] }>("/teams");
      setTeams(response.teams || []);
      setError(null);
    } catch (err: any) {
      setError(err.message || "Failed to load teams");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTeams();
  }, []);

  // Handle create team
  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await apiClient.post("/teams", formData);
      setShowCreateForm(false);
      setFormData({ name: "", description: undefined, managerId: undefined });
      await fetchTeams();
    } catch (err: any) {
      alert(err.message || "Failed to create team");
    }
  };

  // Handle update team
  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingTeam) return;

    try {
      const updates: any = {};
      if (formData.name !== editingTeam.name) updates.name = formData.name;
      if (formData.description !== editingTeam.description)
        updates.description = formData.description;
      if (formData.managerId !== editingTeam.managerId) updates.managerId = formData.managerId;

      await apiClient.patch(`/teams/${editingTeam.id}`, updates);
      setEditingTeam(null);
      setFormData({ name: "", description: undefined, managerId: undefined });
      await fetchTeams();
    } catch (err: any) {
      alert(err.message || "Failed to update team");
    }
  };

  // Handle delete team
  const handleDelete = async (teamId: string) => {
    if (!confirm("Are you sure you want to delete this team?")) return;

    try {
      await apiClient.delete(`/teams/${teamId}`);
      await fetchTeams();
    } catch (err: any) {
      alert(err.message || "Failed to delete team");
    }
  };

  // Start editing
  const startEdit = (team: Team) => {
    setEditingTeam(team);
    setFormData({
      name: team.name,
      description: team.description,
      managerId: team.managerId,
    });
    setShowCreateForm(false);
  };

  // Cancel edit/create
  const cancelForm = () => {
    setShowCreateForm(false);
    setEditingTeam(null);
    setFormData({ name: "", description: undefined, managerId: undefined });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-500">Loading teams...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-slate-900">Team Management</h2>
          <p className="text-sm text-slate-500 mt-1">Manage teams and assign managers</p>
        </div>
        <button
          onClick={() => setShowCreateForm(true)}
          className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
        >
          + Create Team
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Create/Edit Form */}
      {(showCreateForm || editingTeam) && (
        <Card title={editingTeam ? "Edit Team" : "Create New Team"}>
          <form onSubmit={editingTeam ? handleUpdate : handleCreate} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Team Name</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Description (optional)
              </label>
              <textarea
                value={formData.description || ""}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value || undefined })
                }
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                rows={3}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Manager ID (optional)
              </label>
              <input
                type="text"
                value={formData.managerId || ""}
                onChange={(e) =>
                  setFormData({ ...formData, managerId: e.target.value || undefined })
                }
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="User ID of the team manager"
              />
            </div>

            <div className="flex gap-2">
              <button
                type="submit"
                className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
              >
                {editingTeam ? "Update Team" : "Create Team"}
              </button>
              <button
                type="button"
                onClick={cancelForm}
                className="flex-1 px-4 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300 transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </Card>
      )}

      {/* Teams Grid */}
      <div className="grid md:grid-cols-2 gap-4">
        {teams.length === 0 ? (
          <div className="col-span-2 text-center py-12 bg-slate-50 rounded-lg border border-slate-200">
            <p className="text-slate-500">No teams found. Create one to get started.</p>
          </div>
        ) : (
          teams.map((team) => (
            <Card key={team.id} title={team.name} subtitle={team.description}>
              <div className="space-y-3">
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wide">ID</p>
                  <p className="text-sm text-slate-700 font-mono">{team.id}</p>
                </div>
                {team.managerId && (
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wide">Manager ID</p>
                    <p className="text-sm text-slate-700">{team.managerId}</p>
                  </div>
                )}
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wide">Created</p>
                  <p className="text-sm text-slate-700">
                    {team.createdAt ? new Date(team.createdAt).toLocaleDateString() : "N/A"}
                  </p>
                </div>
                <div className="flex gap-2 mt-4">
                  <button
                    onClick={() => startEdit(team)}
                    className="flex-1 px-3 py-1 text-sm bg-indigo-50 text-indigo-600 rounded hover:bg-indigo-100 transition-colors"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(team.id)}
                    className="flex-1 px-3 py-1 text-sm bg-red-50 text-red-600 rounded hover:bg-red-100 transition-colors"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </Card>
          ))
        )}
      </div>
    </div>
  );
}

export default TeamManagement;
